import React, { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { DayPicker } from "react-day-picker";
import { cs } from "date-fns/locale";
import { useLocation, useNavigate } from "react-router-dom";
import "../index.css";

/* ---------------------- Pomocné funkce – svátky, formát -------------------- */

// Výpočet data Velikonoc (Velký pátek + Velikonoční pondělí)
function getEasterDate(year) {
  const f = Math.floor;
  const G = year % 19;
  const C = f(year / 100);
  const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
  const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
  const J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7;
  const L = I - J;
  const month = 3 + f((L + 40) / 44);
  const day = L + 28 - 31 * f(month / 4);
  return new Date(year, month - 1, day);
}

// Kompletní seznam českých svátků (fixní + pohyblivé)
function getCzechHolidays(year) {
  const easter = getEasterDate(year);

  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);

  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);

  return [
    // Fixní
    new Date(year, 0, 1), // 1.1.
    new Date(year, 4, 1), // 1.5.
    new Date(year, 4, 8), // 8.5.
    new Date(year, 6, 5), // 5.7.
    new Date(year, 6, 6), // 6.7.
    new Date(year, 8, 28), // 28.9.
    new Date(year, 9, 28), // 28.10.
    new Date(year, 10, 17), // 17.11.
    new Date(year, 11, 24), // 24.12.
    new Date(year, 11, 25), // 25.12.
    new Date(year, 11, 26), // 26.12.

    // Pohyblivé
    goodFriday,
    easterMonday,
  ];
}

function fmt(d) {
  const dd = d.getDate().toString().padStart(2, "0");
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

/* ---------------------------- Hlavní komponenta ---------------------------- */

export default function LeaveRequestPage() {
  const { user, loading: authLoading } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [selectedDays, setSelectedDays] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [daysTable, setDaysTable] = useState([]);
  const [lastType, setLastType] = useState("dovolena");

  const [note, setNote] = useState("");

  const [approvedDays, setApprovedDays] = useState([]);
  const [pendingDays, setPendingDays] = useState([]);

  const [branchHours, setBranchHours] = useState(null);
  const [baseInfo, setBaseInfo] = useState({
    denniFond: 0,
    dovolenaZbyva: 0,
    prescasyZbyva: 0,
    uvazek: 1,
  });

  const [currentRequest, setCurrentRequest] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const modeParam = searchParams.get("mode") || "create";
  const requestId = searchParams.get("id") || null;

  const allowedModes = ["create", "edit", "resubmit", "change", "cancel"];
  const mode = allowedModes.includes(modeParam) ? modeParam : "create";

  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit";
  const isResubmitMode = mode === "resubmit";
  const isChangeMode = mode === "change";
  const isCancelMode = mode === "cancel";

  /* ----------------------- Svátky – memoizovaný seznam ---------------------- */

  const holidays = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const arr = [];
    for (let y = currentYear - 1; y <= currentYear + 5; y++) {
      arr.push(...getCzechHolidays(y));
    }
    return arr;
  }, []);

  /* ----------------------------- Popisky režimů ----------------------------- */

  const modeTitle = useMemo(() => {
    if (isCreateMode) return "Žádost o volno/směnu";
    if (isEditMode) return "Úprava žádosti o volno/směnu";
    if (isResubmitMode) return "Úprava a odeslání žádosti znovu";
    if (isChangeMode) return "Oprava schválené žádosti";
    if (isCancelMode) return "Žádost o zrušení schválené žádosti";
    return "Žádost o volno/směnu";
  }, [isCreateMode, isEditMode, isResubmitMode, isChangeMode, isCancelMode]);

  const metaLabelByMode = useMemo(() => {
    if (isChangeMode) return "Oprava schválené žádosti";
    if (isCancelMode) return "Zrušení schválené žádosti";
    return currentRequest?.metaLabel || "";
  }, [isChangeMode, isCancelMode, currentRequest]);

  /* ----------------------------- Načtení lidí ------------------------------- */

  useEffect(() => {
    if (!user) return;

    async function loadEmployees() {
      try {
        let url = "";
        if (user.role === "admin" || user.role === "rm") {
          url = `/api/employees/${user.firma}`;
        } else {
          url = `/api/employees/${user.firma}/${user.pharmacyCode}`;
        }

        const res = await fetch(url, { credentials: "include" });
        const data = await res.json();
        setEmployees(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Chyba načítání zaměstnanců:", err);
      } finally {
        setEmployeesLoading(false);
      }
    }

    loadEmployees();
  }, [user]);

  /* --------------------------- Načtení žádosti ------------------------------ */

  useEffect(() => {
    if (!requestId || isCreateMode || !user) return;

    async function loadRequest() {
      try {
        setRequestLoading(true);
        const res = await fetch(`/api/leaves/${requestId}`, {
          credentials: "include",
        });
        if (!res.ok) {
          console.error("Nepodařilo se načíst žádost", res.status);
          return;
        }
        const data = await res.json();
        setCurrentRequest(data || null);
      } catch (err) {
        console.error("Chyba při načítání žádosti:", err);
      } finally {
        setRequestLoading(false);
      }
    }

    loadRequest();
  }, [requestId, isCreateMode, user]);

  /* -------------------------- Načtení obsazených dnů ------------------------ */

  useEffect(() => {
    if (!user || !selectedEmployee) return;

    async function loadBusyDays() {
      try {
        const res = await fetch(
          `/api/leaves/${user.firma}/${selectedEmployee.pharmacyCode}`,
          { credentials: "include" }
        );

        const all = await res.json();
        if (!Array.isArray(all)) return;

        const approved = [];
        const pending = [];

        for (const r of all) {
          for (const d of r.days || []) {
            const ts = new Date(d.date).setHours(0, 0, 0, 0);

            const dayObj = {
              ts,
              requestId: r._id,
              type: d.type,
              hours: d.hours,
              employeeId: r.employeeId,
              employeeName:
                r.employeeName || r.employeeFullName || r.employee?.fullName || "",
              status: r.status,
            };

            const isApprovedEffective =
              r.status === "schváleno" || r.status === "schváleno (oprava)";

            const isPending = r.status === "čeká";

            // schváleno (zrušení) NIKDY nepřidává volno do kalendáře
            // nahrazeno / zrušeno se taky ignorují
            if (isApprovedEffective) {
              approved.push(dayObj);
            }
            if (isPending) {
              pending.push(dayObj);
            }
          }
        }

        setApprovedDays(approved);
        setPendingDays(pending);
      } catch (err) {
        console.error("Chyba načítání obsazených dnů:", err);
      }
    }

    loadBusyDays();
  }, [user, selectedEmployee]);

  /* ------------------------ Načtení otevírací doby -------------------------- */

  useEffect(() => {
    async function loadBranchHours() {
      if (!selectedEmployee || !selectedEmployee.pharmacyCode || !user) {
        setBranchHours(null);
        return;
      }

      try {
        const res = await fetch(
          `/api/users/${user.firma}/${selectedEmployee.pharmacyCode}`,
          { credentials: "include" }
        );
        if (res.ok) {
          const data = await res.json();
          setBranchHours(data.openingHours || null);
        } else {
          setBranchHours(null);
        }
      } catch (err) {
        console.error("Chyba při načítání otevírací doby:", err);
        setBranchHours(null);
      }
    }

    loadBranchHours();
  }, [selectedEmployee, user]);

  /* ------------------------ Předvyplnění při editaci ------------------------ */

  useEffect(() => {
    if (!currentRequest || employees.length === 0) return;

    const empId =
      typeof currentRequest.employeeId === "string"
        ? currentRequest.employeeId
        : currentRequest.employeeId?._id;

    if (!empId) return;

    const emp = employees.find((e) => e._id === empId);
    if (!emp) return;

    setSelectedEmployee(emp);
    setBaseInfo({
      denniFond: emp.denniFond,
      dovolenaZbyva: emp.dovolenaZbyva,
      prescasyZbyva: emp.prescasyZbyva,
      uvazek: emp.uvazek,
    });
  }, [currentRequest, employees]);

  useEffect(() => {
    if (!currentRequest) return;

    const daysArr = Array.isArray(currentRequest.days)
      ? currentRequest.days
      : [];

    if (daysArr.length > 0) {
      const unique = [
        ...new Map(
          daysArr.map((d) => {
            const dateObj = new Date(d.date);
            return [dateObj.toDateString(), dateObj];
          })
        ).values(),
      ].sort((a, b) => a - b);

      setSelectedDays(unique);

      // skočit na "hlavní" měsíc žádosti (nejčastější měsíc z dnů)
      const counts = new Map(); // key "YYYY-MM" -> count
      for (const dt of unique) {
        const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
      let bestKey = null;
      let bestCount = -1;
      for (const [k, c] of counts.entries()) {
        if (c > bestCount) {
          bestCount = c;
          bestKey = k;
        }
      }
      if (bestKey) {
        const [yy, mm] = bestKey.split("-").map(Number);
        setCurrentMonth(new Date(yy, mm - 1, 1));
      }
    }

    setNote(currentRequest.note || "");
  }, [currentRequest]);

  /* ------------------------- Výběr zaměstnance v selectu -------------------- */

  const handleEmployeeSelect = (id) => {
    const emp = employees.find((e) => e._id === id) || null;
    setSelectedEmployee(emp);

    if (emp) {
      setBaseInfo({
        denniFond: emp.denniFond,
        dovolenaZbyva: emp.dovolenaZbyva,
        prescasyZbyva: emp.prescasyZbyva,
        uvazek: emp.uvazek,
      });
    } else {
      setBaseInfo({
        denniFond: 0,
        dovolenaZbyva: 0,
        prescasyZbyva: 0,
        uvazek: 1,
      });
    }

    if (isCreateMode) {
      setSelectedDays([]);
      setDaysTable([]);
    }
  };

  /* ------------------ Přidávání/Odebírání dnů – BEZ přepisování -------------------- */

  useEffect(() => {
    if (!selectedDays) return;

    setDaysTable((prev) => {
      const result = [...prev];

      // 1) Odebrat řádky, které už nejsou ve selectedDays
      const filtered = result.filter((row) =>
        selectedDays.some(
          (d) => d.toDateString() === row.date.toDateString()
        )
      );

      // 2) Přidat nové dny, které ještě nejsou v tabulce
      selectedDays.forEach((d) => {
        const exists = filtered.some(
          (row) => row.date.toDateString() === d.toDateString()
        );
        if (!exists) {
          filtered.push({
            date: d,
            type: lastType,
            hours: getHoursForType(lastType, d),
            note: "",
          });
        }
      });

      // 3) Seřadit podle data vzestupně
      filtered.sort((a, b) => a.date - b.date);

      return filtered;
    });
  }, [selectedDays]);


  /* ------------------------ Otevírací doba → hodin den ---------------------- */

  const getOpenHoursForDate = (date) => {
    if (!branchHours) return baseInfo.denniFond;

    const dayKey = date.getDay().toString(); // 0–6
    const d = branchHours?.[dayKey];

    if (!d || !d.open || !d.close) return 0;

    const [oH, oM] = d.open.split(":").map(Number);
    const [cH, cM] = d.close.split(":").map(Number);

    const openM = oH * 60 + oM;
    const closeM = cH * 60 + cM;

    if (closeM <= openM) return 0;

    return (closeM - openM) / 60;
  };

  /* --------------------------- Úprava jednoho řádku ------------------------- */

  function getHoursForType(type, date) {
    // CELODENNÍ směna
    if (type === "celodenni") {
      // Létající farmaceuti -> vždy denní fond
      if (selectedEmployee?.isFlying) {
        return baseInfo.denniFond;
      }
      return getOpenHoursForDate(date); // reálné otevírací hodiny pobočky
    }

    // RANNÍ / ODPOLEDNÍ / DOVOLENÁ / NV / LÉKAŘ / JINÉ
    return baseInfo.denniFond;
  }


  const updateRow = (index, field, value) => {
    setDaysTable((prev) => {
      const copy = [...prev];

      const date = copy[index].date;

      if (field === "type") {
        copy[index].type = value;
        setLastType(value);

        // přepočítat hodiny podle nového typu
        copy[index].hours = getHoursForType(value, date);
      }

      if (field === "hours") {
        const num = Number(value);
        copy[index].hours = Number.isNaN(num) ? 0 : num;
      }

      if (field === "note") {
        copy[index].note = value;
      }

      return copy;
    });
  };


  /* ----------------------------- Souhrny hodin ------------------------------ */

  const totalHoursDov = daysTable
    .filter((d) => d.type === "dovolena")
    .reduce((s, d) => s + Number(d.hours || 0), 0);

  const totalHoursNV = daysTable
    .filter((d) => d.type === "nahradni-volno")
    .reduce((s, d) => s + Number(d.hours || 0), 0);

  const totalHoursLekar = daysTable
    .filter((d) => d.type === "lekar")
    .reduce((s, d) => s + Number(d.hours || 0), 0);

  const totalHoursAll = totalHoursDov + totalHoursNV + totalHoursLekar;

  const remainingDov = baseInfo.dovolenaZbyva - totalHoursDov;

  /* ------------------------ Odeslání / uložení žádosti ---------------------- */

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      alert("Vyberte zaměstnance.");
      return;
    }
    if (!isCancelMode && daysTable.length === 0) {
      alert("Vyberte alespoň jeden den.");
      return;
    }

    if (!isCancelMode && totalHoursDov > baseInfo.dovolenaZbyva) {
      alert(
        `Nelze vytvořit žádost – překročila by dostupnou dovolenou.\nDostupné: ${baseInfo.dovolenaZbyva} h, požadováno: ${totalHoursDov} h.`
      );
      return;
    }

    const daysPayload = daysTable.map((d) => ({
      date: d.date.toISOString(),
      type: d.type,
      hours: Number(d.hours),
      note: d.note || "",
    }));

    const basePayload = {
      employeeId: selectedEmployee._id,
      pharmacyCode: selectedEmployee.isFlying
        ? "FLY"
        : selectedEmployee.pharmacyCode,
      firma: user.firma,
      note,
      days: daysPayload,
    };

    let url = "/api/leaves";
    let method = "POST";
    let body = { ...basePayload };

    if (!isCreateMode && !requestId) {
      alert("Chybí ID žádosti v URL.");
      return;
    }

    if (isEditMode) {
      url = `/api/leaves/${requestId}`;
      method = "PATCH";
      body = { ...basePayload };
    } else if (isResubmitMode) {
      if (currentRequest?.status === "vráceno") {
        url = `/api/leaves/${requestId}`;
        method = "PATCH";
        body = { ...basePayload, status: "čeká" };
      } else if (currentRequest?.status === "zamítnuto") {
        url = "/api/leaves";
        method = "POST";
        body = { ...basePayload };
      } else {
        url = `/api/leaves/${requestId}`;
        method = "PATCH";
        body = { ...basePayload, status: "čeká" };
      }
    } else if (isChangeMode) {
      url = "/api/leaves";
      method = "POST";
      body = {
        ...basePayload,
        metaLabel: "Oprava schválené žádosti",
        originalRequestId: requestId,
      };
    } else if (isCancelMode) {
      url = "/api/leaves";
      method = "POST";

      // tvrdá pojistka – cancel musí nést původní dny (kvůli mongoose validaci)
      if (!Array.isArray(daysPayload) || daysPayload.length === 0) {
        alert(
          "Nelze odeslat zrušení – chybí původní dny (daysPayload je prázdné). " +
          "Zkuste stránku obnovit nebo počkejte na načtení žádosti."
        );
        return;
      }

      body = {
        ...basePayload,
        days: daysPayload,
        metaLabel: "Zrušení schválené žádosti",
        originalRequestId: requestId,
      };

      console.log("CANCEL SUBMIT payload", body); // debug – ať vidíš, co posíláš
    } else {
      url = "/api/leaves";
      method = "POST";
      body = { ...basePayload };
    }

    try {
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Chyba uložení.");

      if (isEditMode) {
        alert("Žádost byla upravena.");
      } else if (isResubmitMode) {
        alert("Žádost byla upravena a znovu odeslána.");
      } else if (isChangeMode) {
        alert("Opravná žádost byla odeslána.");
      } else if (isCancelMode) {
        alert("Žádost o zrušení byla odeslána.");
      } else {
        alert("Žádost byla odeslána.");
      }

      if (user.role === "admin" || user.role === "rm") {
        navigate("/admin/personal/leaves-overview");
      } else {
        navigate("/personal/leaves-overview");
      }

      setSelectedEmployee(null);
      setSelectedDays([]);
      setDaysTable([]);
      setNote("");
      setBaseInfo({
        denniFond: 0,
        dovolenaZbyva: 0,
        prescasyZbyva: 0,
        uvazek: 1,
      });
      setCurrentRequest(null);
    } catch (err) {
      alert(err.message);
    }
  };

  /* ---------------------------- Formát typů pro tooltip ---------------------------- */

  function formatLeaveType(t) {
    switch (t) {
      case "dovolena": return "Dovolená";
      case "nahradni-volno": return "Náhradní volno";
      case "lekar": return "Lékař";
      case "jine": return "Jiné";
      case "celodenni": return "Celodenní směna";
      case "ranni": return "Ranní směna";
      case "odpo": return "Odpolední směna";
      default: return t;
    }
  }

  /* ---------------------------- Hover tooltip stav ---------------------------- */

  const [hoverInfo, setHoverInfo] = useState(null);
  const calendarRef = useRef(null);


  function showHoverInfo(date, rect) {
    const ts = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

    const pendingForDay = pendingDays.filter((d) => d.ts === ts);
    const approvedForDay = approvedDays.filter((d) => d.ts === ts);

    if (pendingForDay.length === 0 && approvedForDay.length === 0) {
      setHoverInfo(null);
      return;
    }

    setHoverInfo({
      x: rect.left + rect.width + 8,
      y: rect.top - 2,
      date,
      pending: pendingForDay,
      approved: approvedForDay,
    });
  }

  useEffect(() => {
    const root = calendarRef.current;
    if (!root) return;

    let lastDay = null;

    const handleMove = (e) => {
      const cell = e.target.closest(".rdp-day");
      if (!cell || !root.contains(cell)) {
        if (lastDay !== null) {
          setHoverInfo(null);
          lastDay = null;
        }
        return;
      }

      const dayStr = cell.getAttribute("data-day"); // např. "2025-12-24"
      if (!dayStr || dayStr === lastDay) return;
      lastDay = dayStr;

      const date = new Date(dayStr);
      const rect = cell.getBoundingClientRect();
      showHoverInfo(date, rect);
    };

    const handleLeave = () => {
      lastDay = null;
      setHoverInfo(null);
    };

    root.addEventListener("mousemove", handleMove);
    root.addEventListener("mouseleave", handleLeave);

    return () => {
      root.removeEventListener("mousemove", handleMove);
      root.removeEventListener("mouseleave", handleLeave);
    };
  }, [approvedDays, pendingDays]);
  

  /* ----------------------------- DayPicker logika --------------------------- */

  const handleDaySelect = (days) => {
    setSelectedDays(days ?? []);
  };

  const CustomDayContent = (props) => {
    const { date, displayMonth, activeModifiers } = props;
    if (!date || !displayMonth) return null;

    const ts = new Date(date.getFullYear(), date.getMonth(), date.getDate()).setHours(0, 0, 0, 0);

    const isSelected = !!activeModifiers?.selected;
    const isToday = !!activeModifiers?.today;
    const isHoliday = !!activeModifiers?.holiday;
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;

    const hasApproved = approvedDays.some((d) => d.ts === ts);
    const hasPending = pendingDays.some((d) => d.ts === ts);

    /* ---------------------------- ZÁKLADNÍ TŘÍDY ---------------------------- */

    let textColor = "text-gray-900";
    let bgColor = "";

    if (isHoliday) {
      bgColor = "bg-[#f5c6c6]";
      textColor = "text-black";
    }
    if (isWeekend) {
      bgColor = "bg-gray-100";
      textColor = "text-black";
    }

    /* -------------------------- VYBRANÉ DNY --------------------------- */
    if (isSelected) {
      bgColor = "bg-blue-600 text-white";

      if (isHoliday) {
        bgColor = "bg-blue-600 text-white ring-2 ring-red-300";
      }

      if (isWeekend) {
        bgColor = "bg-blue-600 text-white ring-2 ring-gray-300";
      }
    }

    /* -------------------------- DNEŠNÍ DEN --------------------------- */
    let todayWrapperClass = "";
    if (isToday && !isSelected) {
      todayWrapperClass =
        "relative rounded-md border-2 border-blue-600 p-[2px]";
    }

    if (isToday && isSelected) {
      todayWrapperClass =
        "relative rounded-md border-2 border-blue-600 p-[2px] after:absolute after:inset-1 after:rounded-md after:border-2 after:border-white";
    }

    return (
      <div
        className={`relative w-full h-full flex items-center justify-center ${todayWrapperClass}`}
      >
        <div
          className={`w-full h-full flex items-center justify-center rounded-md ${bgColor} ${textColor}`}
        >
          {date.getDate()}
        </div>

        {(hasApproved || hasPending) && (
          <div
            className="absolute bottom-[4px] left-1/2 -translate-x-1/2 flex flex-col items-center space-y-[2px] z-[20]"
            title={
              hasApproved
                ? "Už někdo má schválené volno"
                : hasPending
                ? "Už někdo požádal o volno"
                : ""
            }
          >
            {hasApproved && (
              <div className="h-[3px] w-[16px] bg-green-600 rounded" />
            )}
            {hasPending && (
              <div className="h-[3px] w-[16px] bg-yellow-500 rounded" />
            )}
          </div>
        )}

      </div>
    );
  };


  /* -------------------------------- Loading -------------------------------- */

  if (authLoading || employeesLoading || requestLoading) {
    return <div className="p-6">Načítám...</div>;
  }

  /* --------------------------------  Render  -------------------------------- */

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold mb-4">{modeTitle}</h1>
        {metaLabelByMode && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
            {metaLabelByMode}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <label className="font-semibold block">Zaměstnanec:</label>
        <select
          className="border p-2 rounded w-full max-w-xl"
          value={selectedEmployee?._id || ""}
          onChange={(e) => handleEmployeeSelect(e.target.value)}
          disabled={!isCreateMode && !!currentRequest}
        >
          <option value="">-- Vyberte zaměstnance --</option>
          {employees.map((emp) => (
            <option key={emp._id} value={emp._id}>
              {emp.fullName} ({emp.position}) – {emp.pharmacyCode}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {selectedEmployee ? (
          <div className="p-4 bg-gray-50 border rounded space-y-1">
            <p>
              <span className="font-semibold">Úvazek:</span>{" "}
              {Number(baseInfo.uvazek).toFixed(2)}
            </p>
            <p>
              <span className="font-semibold">Denní fond:</span>{" "}
              {baseInfo.denniFond} h
            </p>
            <p>
              <span className="font-semibold">Zbývá dovolené:</span>{" "}
              {baseInfo.dovolenaZbyva} h
            </p>
            <p>
              <span className="font-semibold">Zbývá přesčasů (NV):</span>{" "}
              {baseInfo.prescasyZbyva} h
            </p>
            <p className="mt-2">
              <span className="font-semibold">Po této žádosti zbývá:</span>{" "}
              <span className={remainingDov < 0 ? "text-red-600" : ""}>
                {remainingDov} h
              </span>
            </p>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 border rounded text-gray-500">
            Vyberte zaměstnance pro zobrazení údajů o úvazku a dovolené.
          </div>
        )}

        <div ref={calendarRef}>
          {/* KALENDÁŘ – DayPicker + indikátory */}
          <DayPicker
            mode="multiple"
            locale={cs}
            weekStartsOn={1}
            showOutsideDays
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            selected={selectedDays}
            onSelect={isCancelMode ? undefined : handleDaySelect}
            disabled={isCancelMode}
            modifiers={{
              selected: selectedDays,
              today: new Date(),
              weekend: (date) => date.getDay() === 0 || date.getDay() === 6,
              holiday: (date) => {
                const ts = new Date(
                  date.getFullYear(),
                  date.getMonth(),
                  date.getDate()
                ).getTime();

                return holidays.some(
                  (h) =>
                    new Date(
                      h.getFullYear(),
                      h.getMonth(),
                      h.getDate()
                    ).getTime() === ts
                );
              },
              approved: approvedDays.map((d) => new Date(d.ts)),
              pending: pendingDays.map((d) => new Date(d.ts)),
            }}
            modifiersClassNames={{
              selected: "day-selected",
              today: "day-today",
              weekend: "day-weekend",
              holiday: "day-holiday",
              approved: "day-approved",
              pending: "day-pending",
            }}
          />
        </div>
      </div>

      {daysTable.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-semibold">Vybrané dny</h2>
          <table className="w-full border mt-2 text-center text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2">Datum</th>
                <th className="border p-2">Typ</th>
                <th className="border p-2">Hodin</th>
                <th className="border p-2">Poznámka</th>
              </tr>
            </thead>
            <tbody>
              {daysTable.map((row, i) => (
                <tr key={i}>
                  <td className="border p-2">{fmt(row.date)}</td>
                  <td className="border p-2">
                    <div className="relative">
                      <select
                        className="border rounded px-2 py-1 w-full appearance-none pr-8 cursor-pointer"
                        value={row.type}
                        onChange={(e) => updateRow(i, "type", e.target.value)}
                        disabled={isCancelMode}
                      >
                        
                        <option value="dovolena">Dovolená</option>
                        <option value="lekar">Lékař</option>
                        <option value="nahradni-volno">Náhradní volno</option>
                        <option value="jine">Jiné</option>
                        <option value="ranni">Ranní směna</option>
                        <option value="odpo">Odpolední směna</option>
                        <option value="celodenni">Celodenní směna</option>
                      </select>

                      {/* Šipka — 100% zarovnaná, neodskočí, nezvětší prvek */}
                      <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-500">
                        ▼
                      </div>
                    </div>

                  </td>
                  <td className="border p-2">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      value={row.hours}
                      onChange={(e) => updateRow(i, "hours", Number(e.target.value))}
                      className={`border rounded px-2 py-1 w-20 ${
                        isCancelMode ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""
                      }`}
                      disabled={isCancelMode}
                    />
                  </td>

                  <td className="border p-2">
                    <input
                      type="text"
                      value={row.note || ""}
                      onChange={(e) => updateRow(i, "note", e.target.value)}
                      className={`w-full p-1 border rounded ${
                        isCancelMode ? "bg-gray-100 text-gray-600 cursor-not-allowed" : ""
                      }`}
                      placeholder="Poznámka..."
                      disabled={isCancelMode}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {daysTable.length > 0 && (
        <div className="p-4 bg-gray-50 border rounded space-y-1 text-sm">
          <p>Dovolená celkem: {totalHoursDov} h</p>
          <p>Náhradní volno celkem: {totalHoursNV} h</p>
          <p>Lékař celkem: {totalHoursLekar} h</p>
          <p className="font-semibold mt-2">Celkem hodin: {totalHoursAll} h</p>
        </div>
      )}

      <div className="space-y-1">
        <label className="font-semibold">Poznámka:</label>
        <textarea
          className="border p-2 rounded w-full mt-1 min-h-[80px]"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <button
        className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
        onClick={handleSubmit}
        disabled={!selectedEmployee || (!isCancelMode && daysTable.length === 0)}
      >
        {isEditMode
          ? "Uložit změny"
          : isResubmitMode
          ? "Odeslat znovu"
          : isChangeMode
          ? "Odeslat opravnou žádost"
          : isCancelMode
          ? "Odeslat žádost o zrušení"
          : "Odeslat žádost"}
      </button>
    {/* ---------------------- TOOLTIP PRO PENDING / APPROVED ---------------------- */}
    {hoverInfo && (
      <div
        className="fixed z-[999] bg-white border border-gray-300 shadow-lg rounded p-2 text-[12px] w-64"
        style={{ top: hoverInfo.y, left: hoverInfo.x }}
      >
        {/* Datum */}
        <div className="font-semibold text-[13px] mb-2">
          {hoverInfo.date.toLocaleDateString("cs-CZ")}
        </div>

        {/* ČEKÁJÍCÍ */}
        {hoverInfo.pending.length > 0 && (
          <div className="mb-2">
            <div className="text-yellow-700 font-semibold text-[12px] mb-1">
              Čekající
            </div>

            {hoverInfo.pending.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-yellow-700 leading-tight">
                <div className="w-2 h-2 bg-yellow-500 rounded-full shrink-0" />
                <span className="truncate">{p.employeeName} – {formatLeaveType(p.type)}</span>
              </div>
            ))}
          </div>
        )}

        {/* SCHVÁLENÉ */}
        {hoverInfo.approved.length > 0 && (
          <div>
            <div className="text-green-700 font-semibold text-[12px] mb-1">
              Schválené
            </div>

            {hoverInfo.approved.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-green-700 leading-tight">
                <div className="w-2 h-2 bg-green-500 rounded-full shrink-0" />
                <span className="truncate">{a.employeeName} – {formatLeaveType(a.type)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}

    
    </div>
  );
}